import { Address } from './address';
import { Contact, DeliveryMethod, ContactCommunicationMethod } from './contact';

export enum TenantType {
  Private = 'private',
  Professional = 'professional'
}

/**
 * Optional banking details for a tenant. Used (advisory) by the owner-side
 * open-banking reconciliation to match an incoming transfer back to a contract
 * by the payer's IBAN. Never required.
 */
export interface TenantBankingInfo {
  iban?: string;
}

export class Tenant {
    tenantUID!: string;
    workspaceId!: string;      // Workspace-based architecture
    tenantType!: TenantType;
    tenantName?: string;  // New field for private tenants
    contacts: Contact[] = [];
    companyName?: string;
    vatNumber?: string;
    contactAddress?: Address;  // Changed from billingAddress
    bankingInfo?: TenantBankingInfo;  // Optional IBAN for rent reconciliation
    active?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    createdBy?: string;        // User ID who created the tenant
    lastModifiedBy?: string;   // User ID who last modified the tenant

    // Tenant Portal Invitation fields
    claimStatus?: 'unclaimed' | 'invited' | 'claimed';
    invitationId?: string;
    invitationSentAt?: Date;
    claimedByWorkspaceId?: string;
    claimedByUserIds?: string[];
    claimedAt?: Date;

    constructor(
        tenantUID: string,
        tenantType: TenantType,
        contacts: Contact[] = [],
        tenantName?: string,
        companyName?: string,
        vatNumber?: string,
        contactAddress?: Address,
        active = true,
        createdAt: Date = new Date(),
        updatedAt: Date = new Date(),
        workspaceId?: string,
        createdBy?: string,
        lastModifiedBy?: string,
        claimStatus?: 'unclaimed' | 'invited' | 'claimed',
        invitationId?: string,
        invitationSentAt?: Date,
        claimedByWorkspaceId?: string,
        claimedAt?: Date
    ) {
        this.tenantUID = tenantUID;
        this.tenantType = tenantType;
        this.contacts = contacts;
        this.tenantName = tenantName;
        this.companyName = companyName;
        this.vatNumber = vatNumber;
        this.contactAddress = contactAddress;
        this.active = active;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.workspaceId = workspaceId || '';
        this.createdBy = createdBy;
        this.lastModifiedBy = lastModifiedBy;
        this.claimStatus = claimStatus;
        this.invitationId = invitationId;
        this.invitationSentAt = invitationSentAt;
        this.claimedByWorkspaceId = claimedByWorkspaceId;
        this.claimedAt = claimedAt;
    }

    /**
     * Build a Tenant from a plain or partial payload.
     *
     * PREFER THIS over the positional constructor. That constructor takes 17
     * arguments and still cannot express `bankingInfo` or `claimedByUserIds`,
     * so every positional call silently drops fields — which is exactly how the
     * tenants list ended up showing "not invited" for a tenant who had just
     * been invited: the rebuild passed 11 of the 17 arguments, and
     * `claimStatus` is the 14th (#300).
     */
    static from(source: Partial<Tenant>): Tenant {
        const tenant = new Tenant(
            source.tenantUID ?? '',
            source.tenantType ?? TenantType.Private,
            [],
        );
        // Every own enumerable property, with no field list to fall behind.
        Object.assign(tenant, source);
        // Rehydrate after the assign, which would otherwise put back the raw
        // objects and leave the Contact methods missing.
        tenant.contacts = (source.contacts ?? []).map(c =>
            c instanceof Contact ? c : new Contact(c),
        );
        return tenant;
    }


    /**
     * Get the primary contact for this tenant.
     *
     * Prefers the contact explicitly flagged `isPrimary`; falls back to the
     * first contact for legacy tenants saved before the flag existed.
     */
    getPrimaryContact(): Contact | undefined {
        if (this.contacts.length === 0) return undefined;
        return this.contacts.find(c => c.isPrimary) ?? this.contacts[0];
    }
    
    /**
     * Get all available delivery methods across all tenant contacts
     */
    getAvailableDeliveryMethods(): DeliveryMethod[] {
        const availableMethods = new Set<DeliveryMethod>();
        
        this.contacts.forEach(contact => {
            contact.getEnabledCommunicationMethods().forEach(method => {
                availableMethods.add(method.method);
            });
        });
        
        return Array.from(availableMethods);
    }
    
    /**
     * Check if a specific delivery method is available for any contact
     */
    hasDeliveryMethod(method: DeliveryMethod): boolean {
        return this.contacts.some(contact => contact.hasDeliveryMethod(method));
    }
    
    /**
     * Get all contacts that support a specific delivery method
     */
    getContactsWithDeliveryMethod(method: DeliveryMethod): Contact[] {
        return this.contacts.filter(contact => contact.hasDeliveryMethod(method));
    }
    
    /**
     * Get communication method configuration for a specific contact and delivery method
     */
    getCommunicationMethodConfig(contactIndex: number, method: DeliveryMethod): ContactCommunicationMethod | undefined {
        if (contactIndex >= 0 && contactIndex < this.contacts.length) {
            const contact = this.contacts[contactIndex];
            return contact.communicationMethods.find(cm => cm.method === method);
        }
        return undefined;
    }
    
    /**
     * Update communication method for a specific contact
     */
    updateContactCommunicationMethod(contactIndex: number, method: DeliveryMethod, config: Partial<ContactCommunicationMethod>): void {
        if (contactIndex >= 0 && contactIndex < this.contacts.length) {
            this.contacts[contactIndex].updateCommunicationMethod(method, config);
        }
    }
}